import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { ConsentDetailsType } from '../../services/configApi/forms/Consent/ConsentServices';

export interface ConsentContextType {
  consentDataJSON: ConsentDetailsType | null;
  selectedVersionNo: number;
  selectedUiVersionNo: number;
  updateConsentJSON: (consentJson: ConsentDetailsType) => void;
  updateConsentVersionNo: (no: number) => void;
  updateConsentUiVersionNo: (no: number) => void;
}

export const ConsentContext = createContext<ConsentContextType>({
  consentDataJSON: null,
  selectedVersionNo: 0,
  selectedUiVersionNo: 1,
  updateConsentJSON: () => {},
  updateConsentVersionNo: () => {},
  updateConsentUiVersionNo: () => {},
});

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider = ({ children }: ConsentProviderProps) => {
  const [consentDataJSON, setConsentDataJSON] =
    useState<ConsentDetailsType | null>(() => {
      const savedData = localStorage.getItem('consentDataJSON');
      return savedData ? JSON.parse(savedData) : null;
    });

  const [selectedVersionNo, setSelectedVersionNo] = useState<number>(() => {
    const savedVersionNo = localStorage.getItem('selectedVersionNo');
    return savedVersionNo ? +savedVersionNo : 0;
  });

  const [selectedUiVersionNo, setSelectedUiVersionNo] = useState<number>(1);

  const updateConsentUiVersionNo = (no: number) => {
    setSelectedUiVersionNo(no);
  };

  const updateConsentJSON = (consentJson: ConsentDetailsType) => {
    setConsentDataJSON(consentJson);
  };

  const updateConsentVersionNo = (no: number) => {
    setSelectedVersionNo(no);
  };

  // Save consentDataJSON to localStorage whenever it changes
  useEffect(() => {
    if (consentDataJSON !== null) {
      localStorage.setItem('consentDataJSON', JSON.stringify(consentDataJSON));
      localStorage.setItem('selectedVersionNo', selectedVersionNo.toString());
    }
  }, [consentDataJSON]);

  const contextValue = useMemo(
    () => ({
      consentDataJSON,
      selectedVersionNo,
      selectedUiVersionNo,
      updateConsentJSON,
      updateConsentVersionNo,
      updateConsentUiVersionNo,
    }),
    [
      consentDataJSON,
      selectedVersionNo,
      selectedUiVersionNo,
      updateConsentJSON,
      updateConsentVersionNo,
      updateConsentUiVersionNo,
    ],
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};
